// src/pages/chat/ChatPage.jsx
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styles from './ChatPage.module.css';
import ChatSidebar from '../../components/chat/ChatSidebar';
import ChatWindow from '../../components/chat/ChatWindow';
import RequestWindow from '../../components/chat/RequestWindow';
import SentRequestWindow from '../../components/chat/SentRequestWindow';
import Header from '../../components/Header';
import { useGetProfileQuery } from '../../store/api/profileApi';
import signalRService from '../../services/signalrService';
import {
  useGetChatsQuery,
  useGetPendingRequestsQuery,
  useGetSentRequestsQuery,
  useGetAcceptedRequestsQuery,
} from '../../store/api/chatApi';

export default function ChatPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // Optional URL param: /messages/:userId
  const { userId: urlUserId } = useParams();

  // ── Data fetching ─────────────────────────────────────────────────────────
  const { data: currentUser, isLoading: profileLoading } = useGetProfileQuery();

  const {
    data: chats = [],
    isLoading: chatsLoading,
    isError: chatsError,
  } = useGetChatsQuery();

  const {
    data: pendingRequests = [],
    isLoading: requestsLoading,
    isError: requestsError,
  } = useGetPendingRequestsQuery();

  const {
    data: sentRequests = [],
    isLoading: sentRequestsLoading,
    isError: sentRequestsError,
  } = useGetSentRequestsQuery();

  const {
    data: acceptedRequests = [],
    isLoading: acceptedLoading,
  } = useGetAcceptedRequestsQuery();

  // ── Local UI state ────────────────────────────────────────────────────────
  const [viewMode, setViewMode] = useState('chat');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newChatUser, setNewChatUser] = useState(null);
  const [isMobilePanelOpen, setIsMobilePanelOpen] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState({
    isConnected: false,
    isConnecting: false,
    hasConnectedOnce: false,
    error: null,
  });

  // Prevent the URL-param effect from firing more than once per mount
  const urlParamHandledRef = useRef(false);
  // Prevent the viewMode-reset effect from running on the very first render
  const isMountedRef = useRef(false);

  const currentUserId =
    useSelector((state) => state.auth.userId) ?? localStorage.getItem('userId');

  // SignalR connection and the global ReceiveMessage handler are owned by
  // App.jsx (via useSignalRChat). ChatPage does NOT register its own handler —
  // doing so would tear down the global one when this page unmounts.
  //
  // NOTE: mark-as-read is owned entirely by ChatWindow, which fires it only
  // when unreadCount > 0. ChatPage used to fire markAsRead() unconditionally on
  // every selection change, producing a duplicate POST even for already-read
  // conversations. That effect has been removed.

  // ── Handle /messages/:userId URL param ───────────────────────────────────
  //
  // Runs once chats have loaded.
  //   - Existing chat  → select it directly.
  //   - No chat yet    → build a temporary placeholder from location.state
  //                      (populated by the profile-page Message button).
  //
  // Profile page should navigate like:
  //   navigate(`/messages/${user.id}`, {
  //     state: { userName: user.name, userPhoto: user.profilePhotoUrl }
  //   });
  useEffect(() => {
    if (!urlUserId || chatsLoading || urlParamHandledRef.current) return;

    urlParamHandledRef.current = true;

    const existingChat = chats.find((c) => c.otherUserId === urlUserId);

    if (existingChat) {
      setSelectedConversation(urlUserId);
      setViewMode('chat');
    } else {
      const { userName, userPhoto } = location.state || {};
      setNewChatUser({
        id: urlUserId,
        name: userName || 'User',
        profilePhotoUrl: userPhoto || null,
        isNewChat: true,
      });
      setSelectedConversation(urlUserId);
      setViewMode('chat');
    }

    setIsMobilePanelOpen(true);

    // Strip navigation state so a hard-refresh doesn't re-apply it
    window.history.replaceState({}, document.title);
  }, [urlUserId, chats, chatsLoading, location.state]);

  /* ── Legacy: location.state?.newChatUserId (kept for other callers) ── */
  useEffect(() => {
    // URL param takes priority
    if (urlUserId || !location.state?.newChatUserId) return;

    const { newChatUserId, userName, userPhoto } = location.state;
    const existingChat = chats.find((c) => c.otherUserId === newChatUserId);

    if (existingChat) {
      setSelectedConversation(newChatUserId);
    } else {
      setNewChatUser({
        id: newChatUserId,
        name: userName,
        profilePhotoUrl: userPhoto,
        isNewChat: true,
      });
      setSelectedConversation(newChatUserId);
    }

    setViewMode('chat');
    setIsMobilePanelOpen(true);
    window.history.replaceState({}, document.title);
  }, [location.state, chats, urlUserId]);

  // ── selectChat custom event (emitted by SentRequestWindow "Open Chat") ────
  useEffect(() => {
    const handleSelectChat = ({ detail: { userId } }) => {
      setSelectedConversation(userId);
      setViewMode('chat');
      setIsMobilePanelOpen(true);
      navigate(`/messages/${userId}`, { replace: true });
    };

    window.addEventListener('selectChat', handleSelectChat);
    return () => window.removeEventListener('selectChat', handleSelectChat);
  }, [navigate]);

  // ── Monitor SignalR connection state ──────────────────────────────────────
  useEffect(() => {
    if (!currentUserId) return;
    const connection = signalRService.connection;
    if (!connection) return;

    const onReconnecting = (error) =>
      setConnectionStatus((prev) => ({
        ...prev,
        isConnected: false,
        isConnecting: prev.hasConnectedOnce,
        error: error?.message ?? 'Reconnecting…',
      }));

    const onReconnected = () =>
      setConnectionStatus({
        isConnected: true,
        isConnecting: false,
        hasConnectedOnce: true,
        error: null,
      });

    const onClose = (error) =>
      setConnectionStatus((prev) => ({
        ...prev,
        isConnected: false,
        isConnecting: false,
        error: error?.message ?? 'Connection lost',
      }));

    connection.onreconnecting(onReconnecting);
    connection.onreconnected(onReconnected);
    connection.onclose(onClose);

    if (connection.state === 'Connected') {
      setConnectionStatus({
        isConnected: true,
        isConnecting: false,
        hasConnectedOnce: true,
        error: null,
      });
    }

    return () => {
      // SignalR's API doesn't support removing individual event-level callbacks
      // for onreconnecting / onreconnected / onclose, so we do nothing here.
      // The handlers are no-ops once `isMounted` is false in real usage.
    };
  }, [currentUserId]);

  // ── Request browser notification permission ───────────────────────────────
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // ── Reset selection when the user switches between Chats / Requests / Sent ─
  // Only resets local state — never triggers a route change, because tab
  // switching is pure UI state and must not affect the URL.
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    setSelectedConversation(null);
    setNewChatUser(null);
    setIsMobilePanelOpen(false);
    urlParamHandledRef.current = false;
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Clear newChatUser placeholder once the backend surfaces the chat ───────
  useEffect(() => {
    if (newChatUser && chats.some((c) => c.otherUserId === newChatUser.id)) {
      setNewChatUser(null);
    }
  }, [chats, newChatUser]);

  // ── Derived data ──────────────────────────────────────────────────────────
  const existingChatIds = useMemo(
    () => new Set(chats.map((c) => c.otherUserId)),
    [chats]
  );

  const chatUsers = useMemo(() => {
    const activeChats = chats.map((chat) => ({
      id: chat.otherUserId,
      name: chat.userName,
      profilePhotoUrl: chat.profilePhotoUrl,
      lastMessage: chat.lastMessageText,
      lastSentAt: chat.lastSentAt,
      unreadCount: chat.unreadCount ?? 0,
      isOnline: false,
    }));

    // Users whose request was accepted but no message exchanged yet
    const acceptedWithoutChat = acceptedRequests
      .filter((req) => req.otherUserId && !existingChatIds.has(req.otherUserId))
      .map((req) => ({
        id: req.otherUserId,
        name: req.userName,
        profilePhotoUrl: req.profilePhotoUrl,
        lastMessage: req.firstMessage || 'Start a conversation',
        lastSentAt: req.acceptedAt,
        unreadCount: 0,
        isOnline: false,
        isAcceptedNew: true,
      }));

    return [...activeChats, ...acceptedWithoutChat];
  }, [chats, acceptedRequests, existingChatIds]);

  const requestUsers = useMemo(
    () =>
      pendingRequests.map((request) => ({
        id: request.senderId,
        requestId: request.id,
        name: request.senderUserName,
        profilePhotoUrl: request.senderProfilePhoto,
        lastMessage: request.message,
        lastSentAt: request.sentAt,
        unreadCount: 1,
        isOnline: false,
      })),
    [pendingRequests]
  );

  const sentRequestUsers = useMemo(
    () =>
      sentRequests.map((request) => ({
        id: request.receiverId,
        requestId: request.id,
        name: request.receiverName,
        profilePhotoUrl: request.receiverPhotoUrl,
        lastMessage: request.message,
        lastSentAt: request.createdAt,
        status: request.status,
        respondedAt: request.respondedAt,
        unreadCount: 0,
        isOnline: false,
      })),
    [sentRequests]
  );

  /** The full user object for the currently-selected conversation. */
  const selectedUser = useMemo(() => {
    const pool =
      viewMode === 'chat'
        ? chatUsers
        : viewMode === 'request'
        ? requestUsers
        : sentRequestUsers;

    const found = pool.find((u) => u.id === selectedConversation);
    if (found) return found;
    // Fall back to the new-chat placeholder if nothing matched in the pool
    if (newChatUser?.id === selectedConversation) return newChatUser;
    return null;
  }, [viewMode, chatUsers, requestUsers, sentRequestUsers, selectedConversation, newChatUser]);

  // ── Event handlers ────────────────────────────────────────────────────────

  // ── Chat tab: navigate to /messages/:userId so the URL reflects the open chat.
  // This lets the user bookmark/share/reload a specific conversation.
  const handleSelectConversation = useCallback(
    (userId) => {
      setSelectedConversation(userId);
      setIsMobilePanelOpen(true);
      navigate(`/messages/${userId}`, { replace: true });
    },
    [navigate]
  );

  // ── Request / Sent tabs: selecting a user is purely local UI state.
  // Navigating to /messages/:userId would switch viewMode back to 'chat'
  // (the URL-param effect) and lose the request/sent context entirely.
  const handleSelectRequest = useCallback(
    (userId) => {
      setSelectedConversation(userId);
      setIsMobilePanelOpen(true);
      // Intentionally NO navigate() call here — URL stays as /chat or /messages
    },
    []
  );

  const handleNewUserSelected = useCallback((userInfo) => {
    setNewChatUser({
      id: userInfo.id,
      name: userInfo.name,
      profilePhotoUrl: userInfo.profilePhotoUrl,
      isNewChat: true,
    });
  }, []);

  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode);
  }, []);

  const handleRequestAccepted = useCallback(() => {
    setViewMode('chat');
    setSelectedConversation(null);
    setNewChatUser(null);
    setIsMobilePanelOpen(false);
  }, []);

  const handleMobileBack = useCallback(() => {
    setIsMobilePanelOpen(false);
    setSelectedConversation(null);
    // Only push a route change when the URL actually contains a userId param
    // to avoid navigating away from a bare /messages route.
    if (urlUserId) {
      navigate('/chat', { replace: true }); // FIX: was '/chat'
    }
  }, [navigate, urlUserId]);

  const handleFirstMessageSent = useCallback(() => {
  setViewMode('sent');
  setSelectedConversation(null);
  setNewChatUser(null);
  setIsMobilePanelOpen(false);
}, []);

  // ── Derived loading / error state ─────────────────────────────────────────
  const isLoading =
    profileLoading ||
    chatsLoading ||
    requestsLoading ||
    sentRequestsLoading ||
    acceptedLoading;

  const hasError = chatsError || requestsError || sentRequestsError;

  // ── Early renders ─────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className={styles.container}>
        <Header />
        <div className={styles.loading}>
          <div className={styles.spinner} role="status" aria-label="Loading chat" />
          <p>Loading chat...</p>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className={styles.container}>
        <Header />
        <div className={styles.error}>
          <div className={styles.errorIcon} aria-hidden="true">⚠️</div>
          <h3>Failed to load chat data</h3>
          <p>Please refresh the page to try again.</p>
          <button onClick={() => window.location.reload()} className={styles.retryBtn}>
            🔄 Refresh
          </button>
        </div>
      </div>
    );
  }

  const sidebarUsers =
    viewMode === 'chat'
      ? chatUsers
      : viewMode === 'request'
      ? requestUsers
      : sentRequestUsers;

  // Always sourced from chatUsers regardless of the active tab.
  // Using sidebarUsers would pick up requestUsers' unreadCount:1 entries
  // when on the request tab, making the chat badge show the request count.
  const chatUnreadCount = chatUsers.reduce(
    (sum, u) => sum + (u.unreadCount ?? 0),
    0
  );

  
  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={styles.container}>
      <Header />

      {connectionStatus.isConnecting && (
        <div className={styles.connectionBanner} role="status">
          ⏳ Connecting to chat server...
        </div>
      )}

      <div className={styles.chatContainer}>
        <ChatSidebar
          currentUser={currentUser}
          users={sidebarUsers}
          selectedConversation={selectedConversation}
          chatUnreadCount={chatUnreadCount}
          onSelectConversation={
            // Chat tab navigates to /messages/:userId.
            // Request / Sent tabs only update local state — no URL change.
            viewMode === 'chat' ? handleSelectConversation : handleSelectRequest
          }
          onNewUserSelected={handleNewUserSelected}
          connectionStatus={connectionStatus}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          pendingRequestsCount={pendingRequests.length}
          isMobilePanelOpen={isMobilePanelOpen}
        />

        {viewMode === 'chat' ? (
          <ChatWindow
            currentUserId={currentUserId}
            conversationId={selectedConversation}
            selectedUser={selectedUser}
            isNewChat={selectedUser?.isNewChat || selectedUser?.isAcceptedNew}
            isMobileVisible={isMobilePanelOpen}
            onMobileBack={handleMobileBack}
            onFirstMessageSent={handleFirstMessageSent}
          />
        ) : viewMode === 'request' ? (
          <RequestWindow
            currentUserId={currentUserId}
            selectedUser={selectedUser}
            onRequestAccepted={handleRequestAccepted}
            isMobileVisible={isMobilePanelOpen}
            onMobileBack={handleMobileBack}
          />
        ) : (
          <SentRequestWindow
            currentUserId={currentUserId}
            selectedUser={selectedUser}
            isMobileVisible={isMobilePanelOpen}
            onMobileBack={handleMobileBack}
          />
        )}
      </div>
    </div>
  );
}